import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import CustomToast from '@/components/CustomToast';
import NotebookImageUploader from '@/components/Personalize/NotebookImageUploader';
import { LanguageTranslations } from '@/config/language';

const ModalOverlay = ({ children, motionKey }: { children: React.ReactNode; motionKey: string }) => (
  <AnimatePresence>
    <motion.div
      key={motionKey}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="hidden absolute inset-0 bg-black/40 z-[150] lg:flex items-center justify-center"
    >
      {children}
    </motion.div>
  </AnimatePresence>
);

const CloseButton = ({ onClick }: { onClick: () => void }) => (
  <button className="bg-[#93A3B03D] text-xl p-2 rounded-md" onClick={onClick}>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.23742 7.00023L13.7439 1.88765C14.0857 1.57026 14.0857 1.05566 13.7439 0.738291C13.4021 0.420895 12.8479 0.420895 12.5061 0.738291L7.00022 5.8514L1.49438 0.738291C1.15257 0.420895 0.598385 0.420895 0.256602 0.738291C-0.0851811 1.05569 -0.0852084 1.57028 0.256602 1.88765L5.76302 7.00023L0.256602 12.1128C-0.0852084 12.4302 -0.0852084 12.9448 0.256602 13.2622C0.598412 13.5796 1.15259 13.5796 1.49438 13.2622L7.00022 8.14905L12.5061 13.2622C12.8479 13.5796 13.4021 13.5796 13.7438 13.2622C14.0857 12.9448 14.0857 12.4302 13.7438 12.1128L8.23742 7.00023Z"
        fill="black"
      />
    </svg>
  </button>
);

interface NotebookDesktopModalsProps {
  pageTypeModal: boolean;
  setPageTypeModal: (v: boolean) => void;
  nameModal: boolean;
  setNameModal: (v: boolean) => void;
  noOfBooksModal: boolean;
  setNoOfBooksModal: (v: boolean) => void;
  is_edit: string;
  pageOptions: { label: string; value: string }[];
  productLangauges: string[];
  bookPageType: string | null;
  setBookPageType: (v: string) => void;
  setBookPageTypeArabic: (v: string) => void;
  setInputLanguage: (v: keyof LanguageTranslations) => void;
  markOptionCompleted: (id: string, completed: boolean) => void;
  inputValues: Record<string, any>;
  UpdateValue: (key: string, value: string) => void;
  setChildName: (v: string) => void;
  hasImage: boolean;
  croppedImage: string | null;
  setCroppedImage: (v: any) => void;
  imageSelected: string;
  setImageSelected: (v: string) => void;
  croppedFile: any;
  setCroppedFile: (v: any) => void;
  product: any;
  noOfBooks: number;
  setNoOfBooks: (v: number) => void;
  noOfBookOptions: { label: string; value: number; image: string; price: number }[];
  currencySymbol: string;
}

const NotebookDesktopModals: React.FC<NotebookDesktopModalsProps> = ({
  pageTypeModal,
  setPageTypeModal,
  nameModal,
  setNameModal,
  noOfBooksModal,
  setNoOfBooksModal,
  is_edit,
  pageOptions,
  productLangauges,
  bookPageType,
  setBookPageType,
  setBookPageTypeArabic,
  setInputLanguage,
  markOptionCompleted,
  inputValues,
  UpdateValue,
  setChildName,
  hasImage,
  croppedImage,
  setCroppedImage,
  imageSelected,
  setImageSelected,
  croppedFile,
  setCroppedFile,
  product,
  noOfBooks,
  setNoOfBooks,
  noOfBookOptions,
  currencySymbol,
}) => {
  const { t } = useTranslation('personalizeModal');
  const { locale } = useRouter();

  const selectPageType = (value: string, label: string) => {
    setBookPageType(value);
    setBookPageTypeArabic(label);
    setInputLanguage(value as keyof LanguageTranslations);
  };

  return (
    <>
      {pageTypeModal && (
        <ModalOverlay motionKey="page-type-modal">
          <div className="bg-white rounded-lg max-w-sm w-full text-center shadow-lg lg:min-w-[700px] max-h-[97vh] overflow-y-auto scrollbar-hide">
            <div className="relative flex flex-col items-center w-full h-full px-8 py-4">
              <div className="flex w-full items-center justify-end sticky top-0 right-0 z-20 bg-white py-2 my-2" dir="ltr">
                <CloseButton onClick={() => setPageTypeModal(false)} />
              </div>
              <div className="overflow-y-auto flex flex-col gap-3 items-center w-full px-4 pt-2 h-[500px] scrollbar-hide pb-6">
                <h2 className="text-lg font-bold text-primary text-start">{t('pageTypeModalHeading')}</h2>
                <div className="grid grid-cols-2 gap-4 mt-6 w-full">
                  {(is_edit === '0'
                    ? pageOptions.filter((opt) => productLangauges.includes(opt.value))
                    : pageOptions
                  ).map((opt) => (
                    <div
                      key={opt.value}
                      onClick={() => selectPageType(opt.value, opt.label)}
                      className={`cursor-pointer py-3 px-4 rounded-lg border text-center text-primary bg-white text-base shadow-md ${
                        bookPageType === opt.value ? 'border-primary' : 'border-gray-300'
                      }`}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              </div>
              <button
                className="w-[50%] p-4 text-white flex items-center justify-center bg-primary mx-auto rounded-lg"
                onClick={() => { markOptionCompleted('pageType', true); setPageTypeModal(false); }}
              >
                {t('continue')}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {nameModal && (
        <ModalOverlay motionKey="name-modal">
          <div
            className="bg-white rounded-lg max-w-sm w-full text-center shadow-lg lg:min-w-[700px] max-h-[97vh] overflow-y-auto scrollbar-hide"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="relative flex flex-col items-center w-full h-full p-4">
              <div className="flex w-full items-center justify-end sticky top-0 right-0 z-20 bg-white py-2 my-2" dir="ltr">
                <CloseButton onClick={() => setNameModal(false)} />
              </div>
              <div className="w-full lg:mx-auto lg:max-w-xl my-6 lg:flex lg:flex-col lg:gap-6">
                <h1 className={`text-2xl font-normal text-black ${locale === 'ar' ? 'text-right' : 'text-left'}`}>
                  {t('nameModalHeading')}
                </h1>
                <div className="inputs flex flex-col gap-4 items-center mt-6 lg:mt-0">
                  {Object.entries(inputValues).map(([key, value]) => {
                    const isEmpty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
                    if (isEmpty && is_edit === '1') return null;
                    return (
                      <div key={key} className="w-full form-group">
                        <label className={`block text-xs ${locale === 'ar' ? 'text-right' : 'text-left'}`}>
                          {key === 'name' && t('inputLabels.name')}
                          {key === 'first_name' && t('inputLabels.firstName')}
                          {key === 'age' && t('inputLabels.age')}
                          {key === 'init' && t('inputLabels.init')}
                        </label>
                        <input
                          type={key === 'age' ? 'number' : 'text'}
                          name={key}
                          min="1"
                          max="100"
                          onChange={(e) => {
                            UpdateValue(key, e.target.value);
                            if (key === 'name') setChildName(e.target.value);
                          }}
                          value={inputValues[key] ?? ''}
                          className="border-1 text-primary border-gray-300 w-full font-semibold placeholder:font-light mt-2 placeholder:opacity-70 py-3 text-xs px-3 focus:ring-1 transition duration-200 focus:ring-black focus:border-transparent rounded-md"
                        />
                      </div>
                    );
                  })}
                </div>
                {hasImage && (
                  <div className="remove-inner-padding">
                    <NotebookImageUploader
                      croppedImage={croppedImage}
                      setCroppedImage={setCroppedImage}
                      imageSelected={imageSelected}
                      setImageSelected={setImageSelected}
                      isEdit={is_edit}
                      product={product}
                      croppedFile={croppedFile}
                      setCroppedFile={setCroppedFile}
                      imageInEdit={croppedImage}
                      hasImage={hasImage}
                    />
                  </div>
                )}
              </div>
              <button
                className="w-[50%] p-4 text-white flex items-center justify-center bg-primary mx-auto rounded-lg mb-4"
                onClick={() => {
                  if (imageSelected === 'yes' && !croppedImage) {
                    toast.custom((tEl) => <CustomToast type="error" t={tEl} message={t('pictureSelectionError')} />);
                    return;
                  }
                  markOptionCompleted('name', true);
                  setNameModal(false);
                }}
              >
                {t('continue')}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {noOfBooksModal && (
        <ModalOverlay motionKey="no-of-books-modal">
          <div
            className="bg-white rounded-lg max-w-sm w-full text-center shadow-lg lg:min-w-[700px] max-h-[97vh] overflow-y-auto scrollbar-hide"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="relative flex flex-col items-center w-full h-full px-8 py-4">
              <div className="flex w-full items-center justify-end sticky top-0 right-0 z-20 bg-white pt-2" dir="ltr">
                <CloseButton onClick={() => setNoOfBooksModal(false)} />
              </div>
              <div className="p-2 space-y-4">
                <h2 className="text-lg font-semibold text-primary text-center">{t('noOfBooksModalHeading')}</h2>
                <div className="grid grid-cols-3 gap-4 my-2">
                  {noOfBookOptions.map((opt) => (
                    <div
                      key={opt.value}
                      onClick={() => setNoOfBooks(opt.value)}
                      className={`cursor-pointer p-4 rounded-lg border text-primary bg-white ${
                        noOfBooks === opt.value ? 'border-primary border-2' : 'border-gray-300'
                      } flex flex-col gap-2 items-center justify-center`}
                    >
                      <Image
                        width={100}
                        height={100}
                        src={opt.image}
                        alt={opt.label}
                        className="rounded-lg w-full"
                        priority
                        unoptimized
                      />
                      <div className="flex items-center justify-between w-full">
                        <h2 className="font-bold text-primary">{opt.label}</h2>
                        <p className="font-normal text-[#FF5470]">
                          {currencySymbol === 'ILS' ? `₪ ${opt.price}` : `${currencySymbol} ${opt.price}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button
                className="w-[70%] p-4 text-white flex items-center justify-center bg-primary mx-auto rounded-lg mt-4"
                onClick={() => { markOptionCompleted('noOfBooks', true); setNoOfBooksModal(false); }}
              >
                {t('continue')}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
};

export default NotebookDesktopModals;
